import { useEffect, useRef, useState } from 'react';

/** Per-character delays. Deleting runs faster than typing, as real ones do. */
const TYPE_MS = 58;
const DELETE_MS = 32;

/**
 * Types a word out one character at a time, and rubs out the previous one first
 * whenever the word changes.
 *
 * Driven by the word itself rather than by a list and a timer of its own: the
 * caller advances the craving, and this follows. That keeps the typing and
 * whatever else is on screen from drifting apart over a long session, which two
 * independent timers reliably do.
 *
 * @param {string} word
 * @param {boolean} enabled  False returns the word whole — see the reduced-motion
 *   note in DemoReel. A stylesheet can flatten a CSS animation but not a timer.
 * @returns {string} The characters visible right now.
 */
export function useTypewriter(word, enabled) {
  const [shown, setShown] = useState(enabled ? '' : word);
  const [erasing, setErasing] = useState(false);
  const previous = useRef(word);

  // A new word means the old one has to come off first.
  useEffect(() => {
    if (!enabled) {
      setShown(word);
      return;
    }
    if (word !== previous.current) setErasing(true);
    previous.current = word;
  }, [word, enabled]);

  useEffect(() => {
    if (!enabled) return;

    if (erasing) {
      if (shown.length === 0) {
        setErasing(false);
        return;
      }
      const timer = window.setTimeout(() => setShown((text) => text.slice(0, -1)), DELETE_MS);
      return () => window.clearTimeout(timer);
    }

    if (shown.length < word.length) {
      const timer = window.setTimeout(
        () => setShown(word.slice(0, shown.length + 1)),
        // A touch of variance, so it reads as typing rather than as a machine
        // emitting characters on a metronome.
        TYPE_MS + (shown.length % 3) * 14
      );
      return () => window.clearTimeout(timer);
    }
  }, [shown, erasing, word, enabled]);

  return shown;
}
